'use client';

/**
 * Shared hook for the capability-coverage cards — calls the
 * `analytics.coverage` strategy and returns the coverage payload,
 * including the Layer-1 chrome `labels`.
 *
 * Composite-payload pattern (klyde-cards § data shape): every card in
 * the mode9_coverage preset calls this hook; React Query dedupes so
 * N cards = 1 BFF call. The response carries `labels` — cards slice
 * their keys via the shared `L` constant; no chrome strings live in
 * TSX (i18n_concept.md line 87).
 *
 * `queryKey` is surfaced on the return so cards pass it straight to
 * `useCardExport({query_key})` — the hook is the single source of
 * truth for the key; cards never reconstruct it.
 *
 * @see klyde_edge/klyde_intelligence/services/analytics/coverage.py — read_coverage
 * @see klyde_edge/ui_config/labels/coverage.py — Layer 1 chrome
 */

import { useQuery } from '@tanstack/react-query';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { api } from '@/lib/api/client';

export interface CoverageRoleCount {
  role: string;
  calls: number;
}

export interface CoverageCapability {
  strategy: string;
  namespace: string;
  category: string;
  scope: string | null;
  mode: string;
  is_meta: boolean;
  calls: number;
  p99_ms: number;
  distinct_roles: number;
  roles: CoverageRoleCount[];
  last_used: string | null;
  used: boolean;
}

export interface CoverageNamespace {
  namespace: string;
  total: number;
  used: number;
  coverage_pct: number;
}

export interface CoverageByRole {
  role: string;
  capabilities_used: number;
  calls: number;
}

export interface CoverageBySource {
  utm_source: string;
  calls: number;
}

export interface CoverageTotals {
  capabilities: number;
  used: number;
  unused: number;
  coverage_pct: number;
}

export type CoverageLabels = Record<string, string>;

export interface CoverageResponse {
  env: string;
  window: { from?: string | null; to?: string | null };
  generated_at?: string | null;
  totals: CoverageTotals;
  by_namespace: CoverageNamespace[];
  by_role: CoverageByRole[];
  by_source: CoverageBySource[];
  capabilities: CoverageCapability[];
  unused: string[];
  labels?: CoverageLabels | null;
}

export function useCoverageView() {
  const strategy = useCapabilityStore((s) => s.strategyName('analytics', 'coverage'));
  const lang = useLanguageStore((s) => s.lang);

  const queryKey = ['analytics-coverage', strategy, lang] as const;
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.getStrategyData<CoverageResponse>(
        strategy ?? 'analytics_coverage',
        { ...(lang ? { lang } : {}) },
      );
      if (!res.success) throw new Error(res.error || `${strategy} failed`);
      return res.data;
    },
    staleTime: 60 * 1000,
  });
  return { ...query, queryKey };
}

/** Loading sentinel for KPI tiles (paintedKpi treats '…' as loading). */
export const KPI_LOADING = '…';

/** "N" → localized integer string; nullish → dash. */
export function fmtInt(n: unknown, dash = '—'): string {
  if (n == null || typeof n !== 'number') return dash;
  return n.toLocaleString();
}

/** Latency ms → painted string: "614 ms" under 1s, "3.2 s" above.
 *  Unit tokens come from the labels dict (chrome lane). */
export function fmtMs(ms: number, unitMs = 'ms', unitS = 's'): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} ${unitS}`;
  return `${Math.round(ms)} ${unitMs}`;
}

/** ISO timestamp → relative freshness string ("just now" / "5m ago" / "2h ago"
 *  / "3d ago"). Suffix tokens are Layer-1 labels. Recomputed on render/refetch;
 *  not a live ticker. Empty string on a missing/invalid stamp. */
export function fmtAgo(iso: string | null | undefined, L: Record<string, string>): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return L.cov_ago_now ?? 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}${L.cov_ago_min ?? 'm ago'}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${L.cov_ago_hr ?? 'h ago'}`;
  return `${Math.floor(h / 24)}${L.cov_ago_day ?? 'd ago'}`;
}
